import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrlWithQuery, buildAuthHeadersForFetch } from "./RestService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import IQBOCustomerDTO from "../models/QuickBooks Online/IQBOCustomerDTO";

import IQBOReprocessOrderRepAssignmentsDTO, {
    IQBOReprocessOrderRepAssignmentsResult,
} from "../models/DTO/IQBOReprocessOrderRepAssignmentsDTO";

const searchCustomers = async (name: string, userModel: IUserUI, supplierModel: ISupplierModel): Promise<IQBOCustomerDTO[]> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.QBOSearchCustomers,
        query: { name },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to search QBO customers: ${response.statusText}`);
    }

    const data = await response.json();
    return data.customers ?? data;
};

// Deadline for one reprocess batch (up to 200 orders). Without it a stalled request would leave the
// caller's batch loop, and the UI it locks, hung forever.
export const REPROCESS_REQUEST_TIMEOUT_MS = 120000;

const reprocessOrderRepAssignments = async (
    payload: IQBOReprocessOrderRepAssignmentsDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IQBOReprocessOrderRepAssignmentsResult> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REPROCESS_REQUEST_TIMEOUT_MS);
    try {
        const response = await fetch(buildRestUrlWithQuery({ endpoint: RestEndpointsEnum.QBOReprocessOrderRepAssignments }), {
            method: "POST",
            headers: buildAuthHeadersForFetch({ userModel, supplierModel }),
            body: JSON.stringify(payload),
            signal: controller.signal,
        });
        if (!response.ok) {
            throw new Error(`Failed to reprocess QBO order assignments (${response.status}): ${response.statusText}`);
        }
        // Body streaming is covered by the same deadline; the timer is cleared only after it is consumed.
        return await response.json();
    } catch (error) {
        if (controller.signal.aborted) {
            throw new Error(`Reprocessing QBO order assignments timed out after ${REPROCESS_REQUEST_TIMEOUT_MS / 1000} seconds.`);
        }
        throw error;
    } finally {
        clearTimeout(timer);
    }
};

const QboService = {
    reprocessOrderRepAssignments,
    searchCustomers,
};

export default QboService;
